import React, {useState} from 'react';
import {Button, Grid, TextField, Typography} from "@mui/material";
import PropTypes from "prop-types";
import {
  formatPhoneNumber,
  parseFormattedPhoneNumber,
  validateName,
  validatePhoneNumber
} from "../../../util/ValidationUtils";
import {Person} from "@mui/icons-material";
import AddressLookup from "../AddressLookup";
import {confirmTermsOfService, requestEmailVerificationToken, updateAccount} from "../../user/request/user-requests";
import {requestLoggedInAuthoritySource} from "../../../services/session/session-requests";
import Busy from "../../Busy";
import {checkAddressForConfirmation, createLocationFromAddress, getGoogleSuppliedAddressParts} from "../../../util/AddressUtil";
import AddressConfirmationDialog from "../../address/AddressConfirmationDialog";
import ChargeModelButton from '../ChargeModelButton';
import PayoutScheduleSelect from '../PayoutScheduleSelect';
import TermsAndPolicyCheckbox from "../../TermsAndPolicyCheckbox";
import UploadLegalAgreement from "../UploadLegalAgreement";
import {createTimestampedFileName, sanitizeFileName, validateFileWithErrorMessage} from "../../../util/FileUtils";
import {deleteFile, uploadCompanyProfileImage, uploadLegalAgreement} from "../../checkin/request/file-upload-requests";
import ViewLegalAgreement from "../ViewLegalAgreement";
import {isSupplierAccount} from "../../../util/AccountUtils";
import SupplierProfileUrl from "../SupplierProfileUrl";
import {useFeatureFlags} from "../../../hooks/routing/useFeatureFlags";
import {
  EditCompanyProfileColumnGap5,
  EditCompanyProfileMarginTop20,
  EditCompanyProfileMarginTop20Button,
  EditCompanyProfileMarginTop20Divider,
  EditCompanyProfileRowGap10
} from "./style";
import {getErrorMessage} from "../../../util/NetworkErrorUtil";
import {INITIAL_SPECIAL_CHARS_REGEX} from "../../../util/sanitizeUtil";
import {useValidateByRegex} from '../../../hooks/validation/useValidateByRegex';

import DropGallery from '../../DropGallery';
import AccountEmailChange from '../AccountEmailChange';
import {useSnackbar} from "../../../context/snackbar-context";

const companyDescriptionsValidations = [
  {
    regexes: [
      INITIAL_SPECIAL_CHARS_REGEX,
    ],
    errorMessage: 'a special character at the beginning'
  },
  {
    regexes: [
      /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/i,
    ],
    errorMessage: 'email addresses.'
  },
  {
    regexes: [
      /\+?1?[-.]?\(?\d{3}\)?[-.\s]?\d{3}[-.]?\d{4}/i,
      /\+\d{1,3}[-\s.]?\(?\d{1,4}\)?[-\s.]?\d{1,4}[-\s.]?\d{1,4}(?:[-\s.]?\d{1,9})?/i,
      /\b\d{3}[-\s.]\d{3}[-\s.]\d{4}\b/i
    ],
    errorMessage: 'phone numbers.'
  },
  {
    regexes: [
      /(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|edu|gov|mil|biz|info|mobi|name|aero|asia|jobs|museum)(?:\/[^\s]*)?/i,
    ],
    errorMessage: 'URLs'
  },
  {
    regexes: [
      /@[A-Za-z0-9_]{2,30}/,
    ],
    errorMessage: 'social media handles'
  },
];

const formatCompanyDescriptionValidationFailures = (validationFailures) => {
  if (!validationFailures || validationFailures.length === 0) {
    return '';
  }
  return `Company Description cannot contain ${validationFailures.join(', ')}`;
}

// The account API rejects updates whose address is missing a city or a state,
// so both are enforced client-side to surface the problem inline instead of as
// a server error after submit.
const CITY_STATE_REQUIRED_MESSAGE = 'Address must include at least a city and state.';

const EditCompanyProfile = ({ account, handleAccountUpdated }) => {
  const { supplierPublicProfile } = useFeatureFlags();
  const { snackbarShowMessage } = useSnackbar();
  const [companyToEdit, setCompanyToEdit] = useState(Object.assign({}, account));
  const [lookupAddress, setLookupAddress] = useState({
    lookupLocation: createLocationFromAddress(account),
    addressLine1: account.addressLine1,
    addressLine2: account.addressLine2,
    city: account.city,
    state: account.state,
    zip: account.zip,
    addressLatitude: account.addressLatitude,
    addressLongitude: account.addressLongitude,
  });
  // Error Messages
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState('');
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState('');
  const [lookupAddressErrorMsg, setLookupAddressErrorMsg] = useState('');
  const [firstNameErrorMsg, setFirstNameErrorMsg] = useState('');
  const [lastNameErrorMsg, setLastNameErrorMsg] = useState('');
  const [legalAgreementErrorMsg, setLegalAgreementErrorMsg] = useState('');

  const [companyDescription, setCompanyDescription] = useState(account.companyDescription || '');
  const companyDescriptionErrors = useValidateByRegex({
    value: companyDescription,
    validations: companyDescriptionsValidations
  });

  const [useCalendarDays, setUseCalendarDays] = useState(account.useCalendarDays);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [legalAgreement, setLegalAgreement] = useState();
  const [legalAgreementFileName, setLegalAgreementFileName] = useState(account.supplierLegalAgreementFileName);

  const [companyLogoDropzone, setCompanyLogoDropzone] = useState({ files: [] });
  const [companyLogoFileName, setCompanyLogoFileName] = useState(account.listingImageFileName);

  const [emailSent, setEmailSent] = useState(false);

  // Holds the typed address and Google's suggested match while the user is
  // being asked to confirm which one to save. Null when no confirmation is pending.
  const [pendingAddressConfirmation, setPendingAddressConfirmation] = useState(null);

  // Builds the AddressLookup state shape from a geocoded address returned by
  // checkAddressForConfirmation. `lookupLocationOverride` lets the caller keep
  // the user's own typed text as the displayed address while still applying
  // Google's resolved address parts/coordinates.
  const buildLookupAddressFromGeocodedAddress = (geocodedAddress, lookupLocationOverride) => ({
    lookupLocation: lookupLocationOverride ?? geocodedAddress.formattedAddress,
    addressLine1: geocodedAddress.addressLine1,
    addressLine2: geocodedAddress.addressLine2,
    city: geocodedAddress.city,
    state: geocodedAddress.state,
    zip: geocodedAddress.zip,
    addressLatitude: geocodedAddress.addressLatitude,
    addressLongitude: geocodedAddress.addressLongitude,
  });

  const simpleTextValidation = (value, label) => {
    if (!value || !value.trim()) {
      return `Please enter a ${label}`;
    }
    if (INITIAL_SPECIAL_CHARS_REGEX.test(value)) {
      return `${label} cannot start with a special character`;
    }
    return '';
  };

  const simpleNameValidation = (value, label) => {
    if (!value || !value.trim()) {
      return `Please enter a ${label}`;
    }
    return validateName(value) ? '' : `${label} can contain only spaces, hyphens, and alphanumeric characters`;
  };

  const phoneNumberValidation = (value) => {
    return validatePhoneNumber(value) ? '' : 'Phone number must be 10 digits';
  };

  const handleCompanyLogoDropzoneUpdate = (dropzone) => {
    setCompanyLogoDropzone(dropzone);
    if (dropzone.files && dropzone.files.length > 0) {
      const file = dropzone.files[0];
      const hasRealCompanyLogoFile = file instanceof File && file.type !== 'fake';
      if (hasRealCompanyLogoFile) {
        setChangesMade(true);
        setCompanyLogoFileName(createTimestampedFileName(sanitizeFileName(dropzone.files[0].name)));
      }
    } else {
      setChangesMade(true);
      setCompanyLogoFileName('');
    }
  };

  const handleSimpleTextChange = (event) => {
    setChangesMade(true);
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case 'companyName':
        setCompanyToEdit({ ...companyToEdit, companyName: value });
        setCompanyNameErrorMsg(simpleTextValidation(value, 'Company Name'));
        break;
      case 'phoneNumber':
        setCompanyToEdit({ ...companyToEdit, phoneNumber: parseFormattedPhoneNumber(value) });
        setPhoneNumberErrorMsg(phoneNumberValidation(value.replace(/[^0-9]*/g,'')));
        break;
      case 'companyDescription':
        setCompanyDescription(value);
        setCompanyToEdit({ ...companyToEdit, companyDescription: value });
        break;
      case 'firstName':
        setCompanyToEdit({ ...companyToEdit, firstName: value });
        setFirstNameErrorMsg(simpleNameValidation(value, 'First Name'));
        break;
      case 'lastName':
        setCompanyToEdit({ ...companyToEdit, lastName: value });
        setLastNameErrorMsg(simpleNameValidation(value, 'Last Name'));
        break;
      default:
        break;
    }
  };

  const handleAddressChange = (lookupAddressObject) => {
    setChangesMade(true);
    // If the user selects a location then clear the error message
    // See validation in form submit handler
    if (lookupAddressObject.lookupLocation && (lookupAddressObject.addressLatitude || lookupAddressObject.addressLongitude)) {
      setLookupAddressErrorMsg('');
    }
    // Clear lat and lng every time the user types in the lookupLocation field as they invalidate the lat and lng.
    // Once the user selects an address from the lookup, then the received lat and lng will be valid.
    setLookupAddress({
      ...lookupAddressObject,
      addressLatitude: lookupAddressObject.addressLatitude || null,
      addressLongitude: lookupAddressObject.addressLongitude || null,
    });
  };

  const handleChangePayoutSchedule = (e) => {
    setChangesMade(true);
    setCompanyToEdit({ ...companyToEdit, payoutSchedule: e.target.value })
  }

  const handleChangeUseCalendarDays = (value) => {
    setChangesMade(true);
    setUseCalendarDays(value);
  }

  const handleTermsCheckbox = (e) => {
    setHasAcceptedTerms(e.target.checked);
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setChangesMade(true);
    if (!file) {
      setLegalAgreement(null);
      setLegalAgreementFileName('');
      setLegalAgreementErrorMsg('');
      return;
    }
    const newName = sanitizeFileName(file.name);
    const newFile = new File([file], newName, { type: file.type });
    const validationMessage = validateFileWithErrorMessage(newFile);
    if (validationMessage) {
      setLegalAgreementErrorMsg(validationMessage);
      return;
    }
    setLegalAgreement(newFile);
    setLegalAgreementFileName(newFile.name);
    setLegalAgreementErrorMsg('');
  };

  // Performs the actual save (Terms of Service confirmation, account update,
  // legal agreement upload, company logo upload). Takes the resolved address
  // explicitly rather than reading `lookupAddress` state directly, since this
  // may be invoked immediately after resolving a manually typed address
  // (silent match or address-confirmation dialog) before that state update
  // has been committed/re-rendered.
  const saveCompanyProfile = (resolvedLookupAddress, addressUserAsserted) => {
    const handleResponseError = (error, message = 'An error occurred while updating your account. Please contact support if this issue persists.', duration = 10000) => {
      snackbarShowMessage(getErrorMessage(error, message), 'error', duration);
      setHasAcceptedTerms(false);
    }

    const body = {
      addressLatitude: resolvedLookupAddress.addressLatitude,
      addressLine1: resolvedLookupAddress.addressLine1,
      addressLine2: resolvedLookupAddress.addressLine2,
      addressLongitude: resolvedLookupAddress.addressLongitude,
      city: resolvedLookupAddress.city,
      companyDescription: companyToEdit.companyDescription?.trim() ?? null,
      companyName: companyToEdit.companyName.trim(),
      email: account.email,
      firstName: companyToEdit.firstName.trim(),
      id: account.id,
      lastName: companyToEdit.lastName.trim(),
      listingImageFileName: companyLogoFileName,
      payoutSchedule: companyToEdit.payoutSchedule,
      phoneNumber: companyToEdit.phoneNumber,
      state: resolvedLookupAddress.state,
      supplierLegalAgreementFileName: legalAgreement ? `${account.id}/${legalAgreementFileName}` : account.supplierLegalAgreementFileName,
      type: account.type,
      useCalendarDays: useCalendarDays,
      username: account.username,
      zip: resolvedLookupAddress.zip,
      addressUserAsserted,
    };

    const tosAgreedAccount = {
      companyName: body.companyName,
      firstName: body.firstName,
      lastName: body.lastName,
      phoneNumber: body.phoneNumber,
      email: body.email,
      password: "",
      confirmPassword: ""
    }

    Busy.set(true);
    confirmTermsOfService(account.id, tosAgreedAccount).then(() => {
      return updateAccount(body).then(() => {
        return requestLoggedInAuthoritySource().then((resp) => {
          handleAccountUpdated(resp.body);
          snackbarShowMessage('Account updated successfully', 'success', 8000);
        }).catch(error => {
          handleResponseError(error);
        }).finally(() => {
          setHasAcceptedTerms(false);
          setChangesMade(false);
        });
      }).catch(error => {
        handleResponseError(error);
      });
    }).catch(error => {
      handleResponseError(error, 'Terms Of Service failed to upload, please try again.', 8000);
    }).finally(() => Busy.set(false));

    if (legalAgreement) {
      uploadLegalAgreement(legalAgreement, legalAgreement.name, legalAgreement.type).then(() => {
        snackbarShowMessage('Legal Agreement uploaded successfully', 'success', 8000);
        setLegalAgreement(null);
        setLegalAgreementFileName('');
        setLegalAgreementErrorMsg('');
      }).catch(() => {
        snackbarShowMessage('Legal Agreement failed to upload, please try again', 'error', 8000);
      });
    }

    if (account.listingImageFileName !== body.listingImageFileName) {
      if (account.listingImageFileName) {
        deleteFile(account.listingImageFileName);
      }

      if (body.listingImageFileName) {
        const file = companyLogoDropzone.files[0];
        uploadCompanyProfileImage(file, body.listingImageFileName, file.type).then(() => {
          snackbarShowMessage('Company logo uploaded successfully', 'success', 8000);
          setCompanyLogoDropzone({ files: [] });
          setCompanyLogoFileName('');
        }).catch(() => {
          snackbarShowMessage('Company logo failed to upload, please try again', 'error', 8000);
        });
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      companyNameErrorMsg ||
      phoneNumberErrorMsg ||
      companyDescriptionErrors.length > 0 ||
      firstNameErrorMsg ||
      lastNameErrorMsg ||
      legalAgreementErrorMsg
    ) {
      return;
    }

    // Address was selected from the Google Places dropdown, so lat/lng are
    // already trustworthy — no manual-entry confirmation needed. The dropdown
    // can still return a broader place than we accept (e.g. picking a state or
    // country suggestion yields coordinates but no city), so the city + state
    // minimum is enforced here too rather than letting the API reject it.
    const addressSelectedFromDropdown = !!(lookupAddress.addressLatitude && lookupAddress.addressLongitude);
    if (addressSelectedFromDropdown) {
      if (!lookupAddress.city || !lookupAddress.state) {
        setLookupAddressErrorMsg(CITY_STATE_REQUIRED_MESSAGE);
        return;
      }
      saveCompanyProfile(lookupAddress, false);
      return;
    }

    if (!lookupAddress.lookupLocation) {
      setLookupAddressErrorMsg('Please select an address from the drop down menu');
      return;
    }

    // Address was typed manually rather than selected from the dropdown —
    // verify it with Google before saving instead of hard-blocking Save.
    // Company Details accepts a City + State-level address as the minimum
    // (unlike the Create/Edit Location wizard, which requires a full street
    // address for an accurate pin location).
    Busy.set(true);
    try {
      const confirmationCheckResult = await checkAddressForConfirmation(lookupAddress.lookupLocation, 'cityState');

      if (confirmationCheckResult.status === 'not-found') {
        setLookupAddressErrorMsg("We couldn't find that address. Please check for typos or select a suggestion from the dropdown.");
        return;
      }

      if (confirmationCheckResult.status === 'incomplete') {
        setLookupAddressErrorMsg(CITY_STATE_REQUIRED_MESSAGE);
        return;
      }

      if (confirmationCheckResult.status === 'match') {
        // Typed address is essentially identical to Google's top suggestion —
        // apply it silently and proceed with save, no modal needed.
        const resolvedLookupAddress = buildLookupAddressFromGeocodedAddress(confirmationCheckResult.address);
        setLookupAddress(resolvedLookupAddress);
        setLookupAddressErrorMsg('');
        saveCompanyProfile(resolvedLookupAddress, false);
        return;
      }

      // status === 'needs-confirmation': pause submit and ask the user to
      // pick between what they typed and Google's suggestion.
      setPendingAddressConfirmation({
        typedAddress: lookupAddress.lookupLocation,
        geocodedAddress: confirmationCheckResult.address,
      });
    } finally {
      Busy.set(false);
    }
  };

  const handleAddressConfirmationCancel = () => {
    // User canceled — do not save, leave the form as-is so they can keep editing.
    setPendingAddressConfirmation(null);
  };

  const handleAddressConfirmationConfirm = (selection) => {
    if (!pendingAddressConfirmation) {
      return;
    }
    const { typedAddress, geocodedAddress } = pendingAddressConfirmation;
    const userKeptTypedAddress = selection === 'typed';
    const resolvedLookupAddress = buildLookupAddressFromGeocodedAddress(
      geocodedAddress,
      userKeptTypedAddress ? typedAddress : undefined
    );

    setLookupAddress(resolvedLookupAddress);
    setLookupAddressErrorMsg('');
    setPendingAddressConfirmation(null);
    saveCompanyProfile(resolvedLookupAddress, userKeptTypedAddress);
  };

  const handleVerificationEmailRequest = () => {
    Busy.set(true);
    requestEmailVerificationToken()
    .then(() => {
      setEmailSent(true);
      snackbarShowMessage('Verification email sent successfully', 'success', 8000);
    }).catch((error) => {
      snackbarShowMessage(getErrorMessage(error), 'error', 10000);
    }).finally(() => {
      Busy.set(false);
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className={'w-100'}>
      <EditCompanyProfileRowGap10 container direction={'column'}>
        <TextField
          variant='standard'
          value={companyToEdit.companyName}
          label={'Company Name'}
          name={'companyName'}
          onChange={handleSimpleTextChange}
          error={!!companyNameErrorMsg}
          helperText={companyNameErrorMsg ? companyNameErrorMsg : ''}
          placeholder={'Please enter a company name'}
          required
          fullWidth
        />
        <TextField
          variant='standard'
          value={account.email}
          label={'Company Email'}
          InputProps={{readOnly: true}}
          fullWidth
        />
        <AccountEmailChange key={`${account.id}:${account.loggedInUsername}`} account={account}/>
        { !account.emailVerified &&
          <Button
            onClick={handleVerificationEmailRequest}
            variant={'contained'}
            color={'secondary'}
            disabled={emailSent}
          >
            Resend Verification Email
          </Button>
        }
        <TextField
          variant='standard'
          value={formatPhoneNumber(companyToEdit.phoneNumber)}
          label={'Phone Number'}
          name={'phoneNumber'}
          type={'tel'}
          onChange={handleSimpleTextChange}
          error={!!phoneNumberErrorMsg}
          helperText={phoneNumberErrorMsg || ''}
          placeholder={'Please enter a phone number'}
          required
          fullWidth
        />
        {
          isSupplierAccount(account) && <EditCompanyProfileMarginTop20 item component={'section'}>
            <Typography variant={'h6'} color={'textPrimary'}>Company Logo</Typography>
            <fieldset className="ss-top ss-dz">
              <DropGallery
                bucket="listing-images"
                imageFilePath={account.listingImageFileName ? account.listingImageFileName : null}
                maxFiles={1}
                updateDropzone={handleCompanyLogoDropzoneUpdate}
              />
            </fieldset>
          </EditCompanyProfileMarginTop20>
        }
        <TextField
          variant='standard'
          value={companyToEdit.companyDescription || ''}
          label={'Company Description'}
          name={'companyDescription'}
          onChange={handleSimpleTextChange}
          placeholder={'Please enter a company description'}
          error={!!(companyDescriptionErrors && companyDescriptionErrors.length)}
          helperText={formatCompanyDescriptionValidationFailures(companyDescriptionErrors)  || 'Describe your company in a few words'}
          multiline
          fullWidth
        />
        {
          isSupplierAccount(account) && <>
            {account.profileUrl && supplierPublicProfile && <SupplierProfileUrl profileUrl={account.profileUrl} />}
            <UploadLegalAgreement handleFileUpload={handleFileUpload} error={!!legalAgreementErrorMsg} helperText={legalAgreementErrorMsg || ''} />
            { !legalAgreementErrorMsg && <ViewLegalAgreement account={account} legalAgreementFile={legalAgreement} /> }
            <PayoutScheduleSelect selection={companyToEdit.payoutSchedule} handleChangeSelection={handleChangePayoutSchedule} />
            <ChargeModelButton useCalendarDays={useCalendarDays} setUseCalendarDays={handleChangeUseCalendarDays} />
          </>
        }
        <EditCompanyProfileRowGap10 item component={'section'}>
          <Typography variant={'h6'} color={'textPrimary'}>Location</Typography>
          <AddressLookup
            name={'address'}
            value={lookupAddress.lookupLocation}
            label={'Address'}
            placeholder={'Please enter your company address'}
            onChange={handleAddressChange}
            error={!!lookupAddressErrorMsg}
            helperText={lookupAddressErrorMsg}
          />
        </EditCompanyProfileRowGap10>
        <EditCompanyProfileRowGap10
          item
          container
          direction={'column'}
          component={'section'}
        >
          <EditCompanyProfileColumnGap5 item container component={'header'} alignContent={'center'}>
            <Person color={'action'} />
            <Typography variant={'h6'}>Personal Information</Typography>
          </EditCompanyProfileColumnGap5>
          <TextField
            variant='standard'
            value={companyToEdit.firstName}
            label={'First Name'}
            name={'firstName'}
            onChange={handleSimpleTextChange}
            error={!!firstNameErrorMsg}
            helperText={firstNameErrorMsg || ''}
            placeholder={'Please enter your first name'}
            required
            fullWidth
          />
          <TextField
            variant='standard'
            value={companyToEdit.lastName}
            label={'Last Name'}
            name={'lastName'}
            onChange={handleSimpleTextChange}
            error={!!lastNameErrorMsg}
            helperText={lastNameErrorMsg || ''}
            placeholder={'Please enter your last name'}
            required
            fullWidth
          />
        </EditCompanyProfileRowGap10>
        <EditCompanyProfileMarginTop20Divider variant={'fullWidth'} />
        <Grid item>
          <TermsAndPolicyCheckbox
            checked={hasAcceptedTerms}
            onChange={handleTermsCheckbox}
            name={"agreementAccepted"}
            disabled={!changesMade}
          />
        </Grid>
        <EditCompanyProfileMarginTop20Button
          type={'submit'}
          variant={'contained'}
          color={'secondary'}
          fullWidth
          disabled={!hasAcceptedTerms}
        >
          Save Changes
        </EditCompanyProfileMarginTop20Button>
      </EditCompanyProfileRowGap10>
      <AddressConfirmationDialog
        open={!!pendingAddressConfirmation}
        typedAddress={pendingAddressConfirmation?.typedAddress || ''}
        suggestedAddress={pendingAddressConfirmation?.geocodedAddress?.formattedAddress || ''}
        googleSuppliedParts={
          pendingAddressConfirmation?.geocodedAddress
            ? getGoogleSuppliedAddressParts(
                pendingAddressConfirmation.typedAddress || '',
                pendingAddressConfirmation.geocodedAddress,
              )
            : []
        }
        onClose={handleAddressConfirmationCancel}
        onConfirm={handleAddressConfirmationConfirm}
      />
    </form>
  );
};

EditCompanyProfile.propTypes = {
  account: PropTypes.object.isRequired,
  handleAccountUpdated: PropTypes.func.isRequired,
};

export default EditCompanyProfile;
